import type { PdfHighlightArea } from './pdfViewerTypes';

export type PdfSelectionCapture =
  | { status: 'none' }
  | { status: 'unmapped'; selectedText: string }
  | { status: 'ok'; selectedText: string; areas: PdfHighlightArea[] };

export type PdfCommentOnSelectionResult =
  | 'opened'
  | 'read-only'
  | 'no-selection'
  | 'no-selectable-text'
  | 'text-layer-error'
  | 'selection-unmapped';

type RectLike = Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom' | 'width' | 'height'>;

function intersects(a: RectLike, b: RectLike): boolean {
  return a.right > b.left && a.left < b.right && a.bottom > b.top && a.top < b.bottom;
}

function toArea(rect: RectLike, pageRect: RectLike, pageIndex: number): PdfHighlightArea {
  const left = Math.max(rect.left, pageRect.left);
  const top = Math.max(rect.top, pageRect.top);
  const right = Math.min(rect.right, pageRect.right);
  const bottom = Math.min(rect.bottom, pageRect.bottom);
  return {
    pageIndex,
    left: ((left - pageRect.left) / pageRect.width) * 100,
    top: ((top - pageRect.top) / pageRect.height) * 100,
    width: ((right - left) / pageRect.width) * 100,
    height: ((bottom - top) / pageRect.height) * 100
  };
}

function rangeRects(range: Range): RectLike[] {
  const rects =
    typeof range.getClientRects === 'function' ? Array.from(range.getClientRects()) : [];
  if (rects.length) return rects;
  const bounding = range.getBoundingClientRect();
  return bounding.width > 0 && bounding.height > 0 ? [bounding] : [];
}

export function capturePdfSelection(
  selection: Selection | null,
  pageElements: Iterable<HTMLElement>
): PdfSelectionCapture {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return { status: 'none' };
  }

  const selectedText = selection.toString().trim();
  if (!selectedText) return { status: 'none' };

  const rects = rangeRects(selection.getRangeAt(0));
  const areas: PdfHighlightArea[] = [];
  for (const page of pageElements) {
    const pageRect = page.getBoundingClientRect();
    if (!pageRect.width || !pageRect.height) continue;
    const pageIndex = Math.max(0, Number.parseInt(page.dataset.pageIndex ?? '0', 10) || 0);
    for (const rect of rects) {
      if (!rect.width || !rect.height || !intersects(rect, pageRect)) continue;
      areas.push(toArea(rect, pageRect, pageIndex));
    }
  }

  return areas.length
    ? { status: 'ok', selectedText, areas }
    : { status: 'unmapped', selectedText };
}

export function pdfCommentSelectionMessage(result: PdfCommentOnSelectionResult): string | null {
  switch (result) {
    case 'opened':
    case 'read-only':
      return null;
    case 'no-selectable-text':
      return 'This PDF has no selectable text. It may be scanned or image-only.';
    case 'text-layer-error':
      return 'The PDF text layer could not be loaded. Refresh the viewer and try again.';
    case 'selection-unmapped':
      return 'The selected text could not be linked to a PDF page. Select text within one or more pages and try again.';
    case 'no-selection':
      return 'Select PDF text first.';
  }
}

export function findPdfHighlightAtPoint(
  highlights: Array<{ id: string; areas: PdfHighlightArea[] }>,
  pageIndex: number,
  leftPercent: number,
  topPercent: number
): string | null {
  for (let highlightIndex = highlights.length - 1; highlightIndex >= 0; highlightIndex -= 1) {
    const highlight = highlights[highlightIndex];
    if (
      highlight.areas.some(
        (area) =>
          area.pageIndex === pageIndex &&
          leftPercent >= area.left &&
          leftPercent <= area.left + area.width &&
          topPercent >= area.top &&
          topPercent <= area.top + area.height
      )
    ) {
      return highlight.id;
    }
  }
  return null;
}
